import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { PAGE_URL } from "../../../constants";
import { ContactDenorm, ContactOrgDenorm } from "../../../store";
import { crm } from "../../../store/cabinetApi/crm";
import { selectDenormalizedData } from "../../../store/crm/selectors";
import { transformMuiDataGridFilterSortToApiModel } from "../../../utils/dataUtils";
import { AppCrmCompaniesListApiArg, Company } from "../../../store/cabinetApi/generated/crm";
import { useDeepCompareEffect } from "react-use";
import { useCabNavigate } from "../../../utils/hooks";
import { useCalendarsConnected, CalendarConnectionStatus } from "../../../store/hooks";
import { Typography, Link } from "@mui/material";


type OrgsApiFilter = Omit<AppCrmCompaniesListApiArg, 'offset'|'limit'>;

const useCompaniesList = ({ pageSize, defaultFilter = {items: []}, defaultSort, search, includeHidden = false }: {
  pageSize: number; defaultFilter?: GridFilterModel; defaultSort?: GridSortModel; search?: string;
  includeHidden?: boolean;
}) => {
  const [orgPage, setOrgPage] = useState(0);
  const [orgsFilter, setOrgsFilter] = useState<GridFilterModel>(defaultFilter || { items: [] });
  const [orgsSort, setOrgsSort] = useState<GridSortModel>(
    defaultSort || [{ field: 'name', sort: 'asc' }]
  );

  // Get calendar connection status
  const calendarStatus = useCalendarsConnected();

  const orgApiFilter: OrgsApiFilter = useMemo(() => {
    return transformMuiDataGridFilterSortToApiModel({ filterModel: orgsFilter, sortModel: orgsSort }, true);
  }, [orgsFilter, orgsSort]);

  const { data: orgData, isFetching: loadingCompanies, refetch: refetchCompanies } = crm.useAppCrmCompaniesListQuery({
    limit: pageSize, offset: orgPage * pageSize, search,
    ...(!includeHidden && { hiddenDateIsnull: true }),
    ...orgApiFilter,
  });

  const [fetchContacts] = crm.useLazyAppCrmContactsListQuery();
  // TODO: remove limit from this request. Only adding it to get the right response shape for now
  const { data: orgCategories } = crm.useAppCrmCompanyCategoriesListQuery({ limit: 1000 });

  const orgs = orgData && selectDenormalizedData(orgData) as ContactOrgDenorm[];

  const [updateCompany] = crm.useAppCrmCompaniesPartialUpdateMutation();

  const handleFetchCompanyContacts = useCallback(async (companyId: ContactOrgDenorm['id']) => {
    const { data: contactData } = await fetchContacts({ limit: 1000, company: companyId }, true);
    return (contactData && selectDenormalizedData(contactData) as ContactDenorm[]) || [];
  }, [fetchContacts]);

  const navigate = useCabNavigate();

  const handleNavigateToOrganization = (orgId: Company['id']) => (
    navigate(`${PAGE_URL.CRM_COMPANIES}/${orgId}`)
  );

  const handleSetOrgsFilter = (filter: GridFilterModel | undefined) => {
    setOrgPage(0);
    setOrgsFilter(filter || { items: [] });
  };

  const handleSetOrgsSort = (sort: GridSortModel | undefined) => {
    setOrgPage(0);
    setOrgsSort(sort || []);
  };

  const handleUpdateCompany = useCallback(async (
    company: Partial<Omit<ContactOrgDenorm, 'primary_contact'>> & { id: ContactOrgDenorm['id'] }
  ) => {
    const res = await updateCompany({ id: company.id, patchedCompany: {
      category: company.category,
      hidden_date: company.hidden_date,
    } });
    if ('data' in res) {
      await refetchCompanies();
      return selectDenormalizedData(res.data) as ContactOrgDenorm;
    }
  }, [refetchCompanies, updateCompany]);

  // Determine appropriate empty message based on calendar connection status
  const emptyMessage = useMemo(() => {
    switch (calendarStatus) {
      case CalendarConnectionStatus.NO_CALENDAR_GRANT:
        return (
          <Typography>
            <Link
              href={PAGE_URL.INTEGRATION_SETTINGS}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Connect your calendar account 
            </Link>
            {" "}to start syncing companies.
          </Typography>
        );

      case CalendarConnectionStatus.NO_LEADER_ASSOCIATIONS:
        return (
          <Typography>
            <Link
              href={PAGE_URL.MANAGE_CALENDARS}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Attach calendars to your teammates
            </Link>
            {" "}to start syncing companies.
          </Typography>
        );

      case CalendarConnectionStatus.CONNECTED:
      default:
        return "No companies to show (if you recently connected calendars we may still be processing them)";
    }
  }, [calendarStatus]);

  useDeepCompareEffect(() => setOrgsFilter(defaultFilter || { items: [] }), [defaultFilter]);
  // reset filter anytime search changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setOrgsFilter(defaultFilter || { items: [] }), [search]);

  return {
    organizations: orgs || [],
    rowCount: orgData?.meta?.count || 0,
    loadingCompanies: loadingCompanies,
    filter: orgsFilter,
    sort: orgsSort,
    categories: orgCategories?.data || [],
    page: orgPage,
    pageSize,
    emptyMessage,
    onNavigateToOrganization: handleNavigateToOrganization,
    onFetchCompanyContacts: handleFetchCompanyContacts,
    onSetFilter: handleSetOrgsFilter,
    onSetSort: handleSetOrgsSort,
    onPageChange: setOrgPage,
    onUpdateCompany: handleUpdateCompany,
  };
};

export default useCompaniesList;
